/**
 * useIsNarrowPhone — reactive check for the narrowest phones the SDK renders on.
 *
 * An axis the tier cannot see: every phone is the fullscreen tier, but the dock
 * is clamped to `100vw - 2 * inset`, so a 320px phone's is 288px wide where a
 * 393px phone's is the full 360. The avatar presenter is bought with bubble
 * width (see
 * {@link ../components/websiteAgentLayout#compactAvatarPresenterSize}), and at
 * 288 the mockup's 160px face leaves a text lane too narrow to read.
 *
 * Same discipline as {@link ./useIsPortrait}: reach for it when the branch has
 * to happen in JS/TS. A purely visual change belongs in a CSS `@media` rule.
 */

import { hasMatchMedia, realMatchMedia } from '../sdk/native-viewport';
import { useMediaQueryFlag } from './useMediaQueryFlag';

/** Below 360px wide. One under, so the query and the threshold cannot drift. */
export const NARROW_PHONE_MAX_WIDTH_PX = 359;

/** The media query this hook is defined by. */
export const NARROW_PHONE_MEDIA_QUERY = `(max-width: ${NARROW_PHONE_MAX_WIDTH_PX}px)`;

/**
 * Synchronous, one-shot read of the current match. Safe to call during
 * render — returns `false` when there's no `window`/`matchMedia` (SSR or a
 * non-DOM test env), which is the roomy default every caller wants. Also the
 * hook's lazy initial state, so the first paint is correct.
 */
export function matchesNarrowPhone(): boolean {
  if (!hasMatchMedia()) return false;
  return !!realMatchMedia(NARROW_PHONE_MEDIA_QUERY)?.matches;
}

/**
 * Returns `true` while the viewport is narrower than 360px, and updates live as
 * the viewport crosses the threshold (resize / rotate).
 */
export function useIsNarrowPhone(): boolean {
  return useMediaQueryFlag(NARROW_PHONE_MEDIA_QUERY, matchesNarrowPhone);
}
